/**
 * CONTRACTS §4 Firestore layout — THE path builders. Every path/collection/
 * group-id in the shared data model is defined exactly once, here. Pure string
 * functions with zero dependencies (core stays dependency-free); callers that
 * need gameId validation apply it at their own boundary (`isValidGameId`).
 *
 * The layout these encode (docs/CONTRACTS.md §4):
 *
 *   users/{uid}                                 platform profile
 *   users/{uid}/games/{gameId}                  per-game aggregates + ranked mirror
 *   users/{uid}/games/{gameId}/events/{auto}    telemetry (batched)
 *   users/{uid}/games/{gameId}/ranked/profile   ranked profile
 *   games/{gameId}/matches/{matchId}            match ledger (server-written)
 *   games/{gameId}/leaderboard/snapshot         precomputed rows (server-written)
 *   games/{gameId}/opponentsV2/{encodedPair}   head-to-head
 *
 * Isolation rule: NOTHING outside `games/{gameId}/**` and
 * `users/{uid}/games/{gameId}/**` may vary per game.
 *
 * Hand-building any of these strings elsewhere is the hazard class that caused
 * the leaderboard join bug (review R6-1) — import the builder instead.
 */
import type { GameId, MatchId, Uid } from './ids.js';

/** The platform users collection root (`users`). Per-game data hangs off each
 *  user doc at {@link userGameAggregatePath}. */
export const USERS_COLLECTION = 'users';

/** Platform profile doc: `users/{uid}` (nickname, avatar, createdAt). */
export function userDocPath(uid: Uid): string {
  return `users/${uid}`;
}

/** Per-user, per-game aggregate doc: `users/{uid}/games/{gameId}`. The ONE doc
 *  the leaderboard recompute scans and the ranked mirror lands on. */
export function userGameAggregatePath(uid: Uid, gameId: GameId): string {
  return `users/${uid}/games/${gameId}`;
}

/** Batched telemetry event log: `users/{uid}/games/{gameId}/events` (docs get
 *  auto ids). */
export function userGameEventsCollectionPath(uid: Uid, gameId: GameId): string {
  return `users/${uid}/games/${gameId}/events`;
}

/**
 * The Firestore collection-GROUP id addressing every `users/*​/games`
 * subcollection at once (`'games'`). CONTRACTS §4 rule this encodes: a
 * collection-group scan's `doc.id` is the {gameId} segment — NOT the uid — so
 * the aggregate writer must stamp explicit `uid` + `gameId` fields on every
 * aggregate doc (see MatchResultWriter) for the scan to filter and attribute
 * rows. Note the string collides with a hypothetical top-level `games`
 * collection: admin adapters must route it via `collectionGroup(...)`.
 */
export function userGamesCollectionGroup(): string {
  return 'games';
}

/** Ranked profile doc: `users/{uid}/games/{gameId}/ranked/profile` — the
 *  ranked subcollection hangs off the per-game aggregate doc. */
export function rankedProfilePath(uid: Uid, gameId: GameId): string {
  return `users/${uid}/games/${gameId}/ranked/profile`;
}

/** Match ledger doc: `games/{gameId}/matches/{matchId}`. Server-written only;
 *  its existence is the exactly-once idempotency guard for match finalize. */
export function matchLedgerPath(gameId: GameId, matchId: MatchId): string {
  return `games/${gameId}/matches/${matchId}`;
}

/** The single precomputed leaderboard snapshot doc for a game:
 *  `games/{gameId}/leaderboard/snapshot`. Server-written only (rules must
 *  reject client writes). */
export function leaderboardSnapshotPath(gameId: GameId): string {
  return `games/${gameId}/leaderboard/snapshot`;
}

/** One document per direction. Encode each ID independently: underscores are
 * valid in player IDs and cannot safely delimit a pair. The SQL adapter maps
 * this versioned transport key onto the existing (game_id, uid, opponent_uid). */
export function headToHeadPath(gameId: GameId, uid: Uid, oppUid: Uid): string {
  return `games/${gameId}/opponentsV2/${encodeURIComponent(uid)}|${encodeURIComponent(oppUid)}`;
}

/** Versioned transport collection; SQL storage is still the same H2H table. */
export function headToHeadCollectionPath(gameId: GameId): string {
  return `games/${gameId}/opponentsV2`;
}
